import React from 'react';
import { FiTrendingUp } from 'react-icons/fi';
import { Mafs, Coordinates, Plot, Point, Text, Polygon, Circle } from 'mafs';
import LessonTemplate from './components/LessonTemplate';
import QuantLessonPracticeSlot from './components/QuantLessonPracticeSlot';

// Visual Components
const BarGraphVisual = () => (
  <div
    style={{
      padding: '1rem',
      background: 'white',
      borderRadius: '8px',
      border: '1px solid var(--liwaza-outline-variant)',
    }}
  >
    <h4
      style={{
        textAlign: 'center',
        marginBottom: '1rem',
        color: 'var(--liwaza-primary)',
      }}
    >
      Bar Graph: Test Scores by Subject
    </h4>
    <Mafs width={500} height={350} viewBox={{ x: [-1, 8], y: [-1, 10] }}>
      <Coordinates.Cartesian
        xAxis={{ lines: false, labels: false }}
        yAxis={{ lines: false, labels: false }}
      />

      {/* Y-axis with labels */}
      <Plot.OfY x={() => 0} color="#6b7280" strokeWidth={2} domain={[0, 9]} />
      {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
        <React.Fragment key={num}>
          <Plot.OfY
            x={() => 0.1}
            color="#6b7280"
            strokeWidth={1}
            domain={[num, num]}
          />
          <Text x={-0.3} y={num} color="#1f2937" size={12} weight="bold">
            {num * 10}
          </Text>
        </React.Fragment>
      ))}

      {/* X-axis */}
      <Plot.OfX y={() => 0} color="#6b7280" strokeWidth={2} domain={[0, 7]} />

      {/* Bars */}
      {[
        { subject: 'Math', score: 8.5, color: '#2563eb', x: 1 },
        { subject: 'Science', score: 7.2, color: '#10b981', x: 2.5 },
        { subject: 'English', score: 6.8, color: '#f59e0b', x: 4 },
        { subject: 'History', score: 7.9, color: '#ef4444', x: 5.5 },
      ].map((item, index) => (
        <React.Fragment key={index}>
          <Polygon
            points={[
              [item.x - 0.4, 0],
              [item.x + 0.4, 0],
              [item.x + 0.4, item.score],
              [item.x - 0.4, item.score],
            ]}
            color={item.color}
            fillOpacity={0.7}
          />
          <Text x={item.x} y={-0.5} color="#1f2937" size={14} weight="bold">
            {item.subject}
          </Text>
          <Text
            x={item.x}
            y={item.score + 0.2}
            color={item.color}
            size={14}
            weight="bold"
          >
            {item.score * 10}
          </Text>
        </React.Fragment>
      ))}

      {/* Axis labels */}
      <Text x={3.5} y={-1.2} color="#1f2937" size={16} weight="bold">
        Subject
      </Text>
      <Text x={-0.8} y={4.5} color="#1f2937" size={16} weight="bold" angle={90}>
        Score
      </Text>

      {/* Title */}
      <Text x={3.5} y={9.5} color="#1f2937" size={18} weight="bold">
        Test Scores by Subject
      </Text>
    </Mafs>
  </div>
);

const LineGraphVisual = () => {
  const tempData = [
    { time: 'Mon', temp: 6.5, x: 1 },
    { time: 'Tue', temp: 7.2, x: 2 },
    { time: 'Wed', temp: 8.1, x: 3 },
    { time: 'Thu', temp: 7.8, x: 4 },
    { time: 'Fri', temp: 8.5, x: 5 },
    { time: 'Sat', temp: 8.9, x: 6 },
  ];

  return (
    <div
      style={{
        padding: '1rem',
        background: 'white',
        borderRadius: '8px',
        border: '1px solid var(--liwaza-outline-variant)',
      }}
    >
      <h4
        style={{
          textAlign: 'center',
          marginBottom: '1rem',
          color: 'var(--liwaza-primary)',
        }}
      >
        Line Graph: Temperature Over Time
      </h4>
      <Mafs width={500} height={350} viewBox={{ x: [-1, 8], y: [-1, 10] }}>
        <Coordinates.Cartesian
          xAxis={{ lines: false, labels: false }}
          yAxis={{ lines: false, labels: false }}
        />

        {/* Grid lines */}
        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((y) => (
          <Plot.OfX
            key={y}
            y={() => y}
            color="#f3f4f6"
            strokeWidth={1}
            domain={[0, 7]}
          />
        ))}
        {[1, 2, 3, 4, 5, 6].map((x) => (
          <Plot.OfY
            key={x}
            x={() => x}
            color="#f3f4f6"
            strokeWidth={1}
            domain={[0, 9]}
          />
        ))}

        {/* Axes */}
        <Plot.OfY x={() => 0} color="#6b7280" strokeWidth={2} domain={[0, 9]} />
        <Plot.OfX y={() => 0} color="#6b7280" strokeWidth={2} domain={[0, 7]} />

        {/* Y-axis labels */}
        {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
          <Text
            key={num}
            x={-0.3}
            y={num}
            color="#1f2937"
            size={12}
            weight="bold"
          >
            {num * 10}°
          </Text>
        ))}

        {/* Line segments */}
        {tempData.slice(0, -1).map((point, index) => (
          <Plot.OfX
            key={index}
            y={(x) => {
              const nextPoint = tempData[index + 1];
              const slope =
                (nextPoint.temp - point.temp) / (nextPoint.x - point.x);
              return point.temp + slope * (x - point.x);
            }}
            color="#2563eb"
            strokeWidth={3}
            domain={[point.x, tempData[index + 1].x]}
          />
        ))}

        {/* Data points */}
        {tempData.map((point, index) => (
          <React.Fragment key={index}>
            <Point x={point.x} y={point.temp} color="#2563eb" />
            <Text x={point.x} y={-0.5} color="#1f2937" size={14} weight="bold">
              {point.time}
            </Text>
            <Text
              x={point.x + 0.2}
              y={point.temp + 0.3}
              color="#2563eb"
              size={10}
            >
              {point.temp * 10}°
            </Text>
          </React.Fragment>
        ))}

        {/* Labels */}
        <Text x={3.5} y={-1.2} color="#1f2937" size={14}>
          Day of Week
        </Text>
        <Text x={-0.8} y={4.5} color="#1f2937" size={14} angle={90}>
          Temperature (°F)
        </Text>
        <Text x={3.5} y={9.5} color="#1f2937" size={16}>
          Weekly Temperature Trend
        </Text>
      </Mafs>
    </div>
  );
};

const PieChartVisual = () => {
  const pieData = [
    { label: 'Study', value: 40, color: '#2563eb', startAngle: 0 },
    { label: 'Sleep', value: 33, color: '#10b981', startAngle: 144 },
    { label: 'Exercise', value: 15, color: '#f59e0b', startAngle: 262.8 },
    { label: 'Leisure', value: 12, color: '#ef4444', startAngle: 316.8 },
  ];

  return (
    <div
      style={{
        padding: '1rem',
        background: 'white',
        borderRadius: '8px',
        border: '1px solid var(--liwaza-outline-variant)',
      }}
    >
      <h4
        style={{
          textAlign: 'center',
          marginBottom: '1rem',
          color: 'var(--liwaza-primary)',
        }}
      >
        Pie Chart: Time Allocation
      </h4>
      <Mafs width={400} height={400} viewBox={{ x: [-5, 5], y: [-5, 5] }}>
        <Coordinates.Cartesian
          xAxis={{ lines: false, labels: false }}
          yAxis={{ lines: false, labels: false }}
        />

        {/* Draw circle for pie chart */}
        <Circle
          center={[0, 0]}
          radius={2.2}
          color="#6b7280"
          strokeWidth={2}
          fillOpacity={0.1}
        />

        {/* Pie slices */}
        {pieData.map((slice, index) => {
          const nextAngle =
            index < pieData.length - 1 ? pieData[index + 1].startAngle : 360;
          const endAngle = nextAngle;
          const midAngle = (slice.startAngle + endAngle) / 2;
          const labelRadius = 2.8;
          const labelX = labelRadius * Math.cos((midAngle * Math.PI) / 180);
          const labelY = labelRadius * Math.sin((midAngle * Math.PI) / 180);

          // Create pie slice polygon
          const slicePoints = [
            [0, 0], // Center
            ...Array.from({ length: 20 }).map((_, i) => {
              const angle =
                slice.startAngle + (i / 19) * (endAngle - slice.startAngle);
              const rad = (angle * Math.PI) / 180;
              return [2.2 * Math.cos(rad), 2.2 * Math.sin(rad)];
            }),
          ];

          return (
            <React.Fragment key={index}>
              <Polygon
                points={slicePoints}
                color={slice.color}
                fillOpacity={0.6}
                strokeWidth={1}
              />
              <Text x={labelX} y={labelY} color={slice.color} size={12}>
                {slice.label}
              </Text>
              <Text x={labelX} y={labelY - 0.3} color={slice.color} size={10}>
                {slice.value}%
              </Text>
            </React.Fragment>
          );
        })}
      </Mafs>
    </div>
  );
};

const DataInterpretationVisual = () => {
  const salesData = [
    { quarter: 'Q1', sales: 4.5, x: 1 },
    { quarter: 'Q2', sales: 6.2, x: 2.5 },
    { quarter: 'Q3', sales: 7.8, x: 4 },
    { quarter: 'Q4', sales: 8.5, x: 5.5 },
  ];

  return (
    <div
      style={{
        padding: '1rem',
        background: 'white',
        borderRadius: '8px',
        border: '1px solid var(--liwaza-outline-variant)',
      }}
    >
      <h4
        style={{
          textAlign: 'center',
          marginBottom: '1rem',
          color: 'var(--liwaza-primary)',
        }}
      >
        Sales Data Analysis
      </h4>
      <Mafs width={500} height={350} viewBox={{ x: [-1, 8], y: [-1, 10] }}>
        <Coordinates.Cartesian
          xAxis={{ lines: false, labels: false }}
          yAxis={{ lines: false, labels: false }}
        />

        {/* Grid */}
        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((y) => (
          <Plot.OfX
            key={y}
            y={() => y}
            color="#f3f4f6"
            strokeWidth={1}
            domain={[0, 7]}
          />
        ))}

        {/* Axes */}
        <Plot.OfY x={() => 0} color="#6b7280" strokeWidth={2} domain={[0, 9]} />
        <Plot.OfX y={() => 0} color="#6b7280" strokeWidth={2} domain={[0, 7]} />

        {/* Bars */}
        {salesData.map((item, index) => (
          <React.Fragment key={index}>
            <Polygon
              points={[
                [item.x - 0.3, 0],
                [item.x + 0.3, 0],
                [item.x + 0.3, item.sales],
                [item.x - 0.3, item.sales],
              ]}
              color="#2563eb"
              fillOpacity={0.7}
            />
            <Text x={item.x} y={-0.5} color="#1f2937" size={11}>
              {item.quarter}
            </Text>
            <Text x={item.x} y={item.sales + 0.2} color="#2563eb" size={10}>
              ${item.sales * 10}M
            </Text>
          </React.Fragment>
        ))}

        {/* Trend line */}
        <Plot.OfX
          y={(x) => 2.1 + 1.15 * x}
          color="#ef4444"
          strokeWidth={2}
          strokeDasharray="5,5"
          domain={[0.5, 6]}
        />

        {/* Y-axis labels */}
        {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
          <Text key={num} x={-0.3} y={num} color="#6b7280" size={10}>
            ${num * 10}M
          </Text>
        ))}

        {/* Analysis annotations */}
        <Text x={1.5} y={8.5} color="#10b981" size={12}>
          ↗ 89% growth from Q1 to Q4
        </Text>
        <Text x={4} y={7} color="#ef4444" size={10}>
          Trend Line
        </Text>

        {/* Labels */}
        <Text x={3.5} y={-1.2} color="#1f2937" size={14}>
          Quarter
        </Text>
        <Text x={-0.8} y={4.5} color="#1f2937" size={14} angle={90}>
          Sales (Millions)
        </Text>
        <Text x={3.5} y={9.5} color="#1f2937" size={16}>
          Quarterly Sales Performance
        </Text>
      </Mafs>
    </div>
  );
};

const GraphsLesson = () => {
  const sections = [
    {
      id: 'bar-graphs',
      title: 'Bar Graphs',
      content: {
        introduction:
          'Bar graphs display categorical data using rectangular bars. The height or length of each bar represents the value for that category.',

        concepts: [
          {
            title: 'Reading Bar Graphs',
            explanation:
              'Bar graphs are excellent for comparing quantities across different categories. Look for patterns, highest/lowest values, and trends.',
            visual: <BarGraphVisual />,
            example: {
              problem:
                'From the test scores bar graph, which subject had the highest score and what was the difference between the highest and lowest scores?',
              solution: [
                'Identify highest score: Math = 85',
                'Identify lowest score: English = 68',
                'Calculate difference: 85 - 68 = 17 points',
                'Math had the highest score with a 17-point spread',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'line-graphs',
      title: 'Line Graphs',
      content: {
        introduction:
          "Line graphs show changes over time by connecting data points with lines. They're ideal for displaying trends and patterns.",

        concepts: [
          {
            title: 'Analyzing Trends',
            explanation:
              'Line graphs reveal trends (increasing, decreasing, stable), cycles, and outliers. The slope indicates rate of change.',
            visual: <LineGraphVisual />,
            example: {
              problem:
                'Based on the temperature line graph, what was the average temperature and describe the overall trend?',
              solution: [
                'Calculate average: (65 + 72 + 81 + 78 + 85 + 89) ÷ 6 ≈ 78.3°F',
                'Overall trend: Generally increasing throughout the week',
                'Steepest increase: Tuesday to Wednesday (+9°F)',
                'Net change: Monday to Saturday = +24°F',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'pie-charts',
      title: 'Pie Charts',
      content: {
        introduction:
          'Pie charts show parts of a whole using sectors of a circle. Each slice represents a proportion of the total.',

        concepts: [
          {
            title: 'Understanding Proportions',
            explanation:
              'Pie charts display percentages that must sum to 100%. Compare slice sizes and calculate missing percentages.',
            visual: <PieChartVisual />,
            example: {
              problem:
                'In the time allocation pie chart, if a student has 20 hours per week for all activities, how many hours are spent studying?',
              solution: [
                'Study represents 40% of total time',
                'Calculate: 40% of 20 hours = 0.40 × 20 = 8 hours',
                'Student spends 8 hours per week studying',
                'Verify: All percentages sum to 100% (40+33+15+12 = 100%)',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'data-interpretation',
      title: 'Data Interpretation',
      content: {
        introduction:
          'Data interpretation involves analyzing graphs and tables to extract meaningful insights, identify patterns, and make calculations.',

        concepts: [
          {
            title: 'Advanced Analysis Techniques',
            explanation:
              'Look for trends, calculate rates of change, compare values, and identify correlations between different data sets.',
            visual: <DataInterpretationVisual />,
            example: {
              problem:
                'Calculate the percentage growth from Q1 to Q4 using the sales data',
              solution: [
                'Q1 sales = $45M, Q4 sales = $85M',
                'Growth = Q4 - Q1 = $85M - $45M = $40M',
                'Percentage growth = §(40/45) × 100%§ = 88.9%',
                'Sales grew by approximately 89% from Q1 to Q4',
              ],
            },
          },
          {
            title: 'Two-Way Tables',
            prerequisiteNote:
              'Read the row label and column label before choosing a denominator.',
            vocabulary: ['row total', 'column total', 'joint count', 'conditional'],
            explanation:
              'For two-way tables, the denominator depends on the wording. “Among students who chose A” uses the A row or column total; “from all students” uses the grand total.',
            microExample: {
              problem:
                'In a table, 12 of 30 juniors prefer Math. What fraction of juniors prefer Math?',
              solution: [
                'The phrase “of juniors” makes 30 the denominator.',
                'Fraction: §12/30 = 2/5§',
              ],
            },
            greTrap:
              'Most two-way table mistakes come from using the grand total when the question asks for a row or column condition.',
            checkQuestion:
              'If a question says “among women,” should your denominator be all people or total women?',
            example: {
              problem:
                'A table shows 18 of 60 total students are seniors, and 9 seniors joined robotics. What percent of seniors joined robotics?',
              solution: [
                'The denominator is seniors, not all students.',
                '§9/18 = 1/2§',
                'Answer: 50%',
              ],
            },
          },
          {
            title: 'Box Plot Reading',
            prerequisiteNote:
              'Use this as a quick chart-reading version; see Statistics for deeper IQR and outlier rules.',
            vocabulary: ['minimum', 'Q1', 'median', 'Q3', 'maximum', 'IQR'],
            explanation:
              'A box plot gives five key values: minimum, Q1, median, Q3, and maximum. The box from Q1 to Q3 contains the middle 50% of the data.',
            microExample: {
              problem:
                'A box plot has Q1 = 20 and Q3 = 50. What is the IQR?',
              solution: ['§IQR = Q3 - Q1 = 50 - 20 = 30§'],
            },
            greTrap:
              'The median is the line inside the box; it is not always centered.',
            checkQuestion:
              'What percent of the data lies between Q1 and Q3?',
            example: {
              problem:
                'A box plot has min=10, Q1=25, median=40, Q3=55, max=80. What is the range and IQR?',
              solution: [
                'Range = max - min = §80 - 10 = 70§',
                'IQR = Q3 - Q1 = §55 - 25 = 30§',
              ],
            },
          },
        ],
      },
    },
  ];

  const practiceQuestions = [
    {
      type: 'timed',
      timeLimit: 90,
      question:
        'A bar graph shows sales for 5 months: Jan(20), Feb(25), Mar(30), Apr(28), May(35). What is the average monthly sales?',
      options: ['27.6', '28', '29', '25.5', '30'],
      correct: 0,
      explanation: 'Average = §(20+25+30+28+35)/5 = 138/5 = 27.6§',
    },
    {
      type: 'timed',
      timeLimit: 120,
      question:
        'The temperature was 70° on Monday and 84° on Tuesday. What was the percentage increase?',
      options: ['20%', '14%', '18%', '25%', '15%'],
      correct: 0,
      explanation:
        'Increase = §84 - 70 = 14§. Percentage increase = §(14 / 70) * 100 = 20%§.',
    },
    {
      type: 'timed',
      timeLimit: 110,
      question:
        'In a pie chart, a section representing 25% of the data would have what central angle?',
      options: ['90°', '45°', '120°', '60°', '75°'],
      correct: 0,
      explanation:
        'A full circle is 360°. The angle for the section is 25% of 360°, which is §0.25 * 360 = 90°§.',
    },
    {
      type: 'timed',
      timeLimit: 100,
      question:
        'A line graph shows the price of stock over 4 days: $10, $12, $9, $15. What was the percent change from day 1 to day 4?',
      options: ['50%', '33%', '25%', '40%', '60%'],
      correct: 0,
      explanation:
        'Change = §$15 - $10 = $5§. Percent change = §(5/10) × 100% = 50%§',
    },
    {
      type: 'timed',
      timeLimit: 95,
      question:
        'A pie chart shows: Math 40%, Science 30%, English 20%, Art 10%. If 200 students were surveyed, how many chose Science?',
      options: ['60', '40', '80', '50', '70'],
      correct: 0,
      explanation: 'Science = §30% of 200 = 0.30 × 200 = 60§ students',
    },
    {
      type: 'timed',
      timeLimit: 125,
      question:
        'A line graph shows company revenue: 2020 = $50K, 2021 = $75K, 2022 = $100K. What is the percent increase from 2020 to 2022?',
      options: ['100%', '50%', '75%', '150%', '200%'],
      correct: 0,
      explanation:
        'Increase = §100 - 50 = 50K§. Percent = §(50/50) × 100% = 100%§.',
    },
    {
      type: 'timed',
      timeLimit: 135,
      question:
        'A pie chart shows: 40% Study, 25% Sleep, 20% Work, 15% Other. If the total is 24 hours, how many hours for Sleep?',
      options: ['6 hours', '5 hours', '4.8 hours', '7 hours', '5.5 hours'],
      correct: 0,
      explanation: '§25% of 24 = 0.25 × 24 = 6§ hours.',
    },
    {
      type: 'timed',
      timeLimit: 120,
      question: 'Which type of graph is best for showing trends over time?',
      options: [
        'Line Graph',
        'Bar Graph',
        'Pie Chart',
        'Scatter Plot',
        'Histogram',
      ],
      correct: 0,
      explanation:
        'Line graphs are ideal for displaying data points over a continuous period, making them perfect for showing trends.',
    },
    {
      type: 'timed',
      timeLimit: 120,
      question:
        'A bar graph shows that Class A has 30 students and Class B has 25 students. How many more students are in Class A?',
      options: ['5', '10', '15', '20', 'Cannot be determined'],
      correct: 0,
      explanation:
        'The difference is found by simple subtraction: §30 - 25 = 5§ students.',
    },
    {
      type: 'timed',
      timeLimit: 120,
      question: 'In a histogram, what does the height of each bar represent?',
      options: ['Frequency', 'Mean', 'Median', 'Range', 'Standard deviation'],
      correct: 0,
      explanation:
        'In a histogram, the height of each bar represents the frequency (count) of values in that interval.',
    },
    {
      type: 'timed',
      timeLimit: 120,
      question: 'What is the main advantage of using a scatter plot?',
      options: [
        'Shows correlation between two variables',
        'Shows parts of a whole',
        'Shows trends over time',
        'Shows frequency distribution',
        'Shows categorical data',
      ],
      correct: 0,
      explanation:
        'Scatter plots are primarily used to visualize the relationship or correlation between two continuous variables.',
    },
    {
      type: 'timed',
      timeLimit: 120,
      question:
        'In a double bar graph, what allows you to compare two sets of data?',
      options: [
        'Side-by-side bars',
        'Different colors only',
        'Different heights only',
        'Legend only',
        'Axis labels only',
      ],
      correct: 0,
      explanation:
        'Double bar graphs use side-by-side bars for each category, allowing direct visual comparison of two data sets.',
    },
    {
      type: 'timed',
      timeLimit: 120,
      question: 'On a coordinate plane, what does the x-intercept represent?',
      options: [
        'Where y = 0',
        'Where x = 0',
        'The slope',
        'The origin',
        'The midpoint',
      ],
      correct: 0,
      explanation:
        'The x-intercept is where the line crosses the x-axis, meaning §y = 0§.',
    },
    {
      type: 'timed',
      timeLimit: 120,
      question: 'A histogram is most similar to which type of graph?',
      options: [
        'Bar graph',
        'Pie chart',
        'Line graph',
        'Scatter plot',
        'Circle graph',
      ],
      correct: 0,
      explanation:
        'A histogram uses bars to display frequency distribution, similar to a bar graph but for continuous data ranges.',
    },

    {
      type: 'untimed',
      timeLimit: 90,
      question:
        'A bar graph shows sales for 5 months: Jan(20), Feb(25), Mar(30), Apr(28), May(35). What is the average monthly sales?',
      options: ['27.6', '28', '29', '25.5', '30'],
      correct: 0,
      explanation: 'Average = §(20+25+30+28+35)/5 = 138/5 = 27.6§',
    },
    {
      type: 'untimed',
      timeLimit: 120,
      question:
        'The temperature was 70° on Monday and 84° on Tuesday. What was the percentage increase?',
      options: ['20%', '14%', '18%', '25%', '15%'],
      correct: 0,
      explanation:
        'Increase = §84 - 70 = 14§. Percentage increase = §(14 / 70) * 100 = 20%§.',
    },
    {
      type: 'untimed',
      timeLimit: 110,
      question:
        'In a pie chart, a section representing 25% of the data would have what central angle?',
      options: ['90°', '45°', '120°', '60°', '75°'],
      correct: 0,
      explanation:
        'A full circle is 360°. The angle for the section is 25% of 360°, which is §0.25 * 360 = 90°§.',
    },
    {
      type: 'untimed',
      timeLimit: 100,
      question:
        'A line graph shows the price of stock over 4 days: $10, $12, $9, $15. What was the percent change from day 1 to day 4?',
      options: ['50%', '33%', '25%', '40%', '60%'],
      correct: 0,
      explanation:
        'Change = §$15 - $10 = $5§. Percent change = §(5/10) × 100% = 50%§',
    },
    {
      type: 'untimed',
      timeLimit: 95,
      question:
        'A pie chart shows: Math 40%, Science 30%, English 20%, Art 10%. If 200 students were surveyed, how many chose Science?',
      options: ['60', '40', '80', '50', '70'],
      correct: 0,
      explanation: 'Science = §30% of 200 = 0.30 × 200 = 60§ students',
    },
    {
      type: 'untimed',
      timeLimit: 125,
      question:
        'A line graph shows company revenue: 2020 = $50K, 2021 = $75K, 2022 = $100K. What is the percent increase from 2020 to 2022?',
      options: ['100%', '50%', '75%', '150%', '200%'],
      correct: 0,
      explanation:
        'Increase = §100 - 50 = 50K§. Percent = §(50/50) × 100% = 100%§.',
    },
    {
      type: 'untimed',
      timeLimit: 135,
      question:
        'A pie chart shows: 40% Study, 25% Sleep, 20% Work, 15% Other. If the total is 24 hours, how many hours for Sleep?',
      options: ['6 hours', '5 hours', '4.8 hours', '7 hours', '5.5 hours'],
      correct: 0,
      explanation: '§25% of 24 = 0.25 × 24 = 6§ hours.',
    },
    {
      type: 'untimed',
      timeLimit: 120,
      question: 'Which type of graph is best for showing trends over time?',
      options: [
        'Line Graph',
        'Bar Graph',
        'Pie Chart',
        'Scatter Plot',
        'Histogram',
      ],
      correct: 0,
      explanation:
        'Line graphs are ideal for displaying data points over a continuous period, making them perfect for showing trends.',
    },
    {
      type: 'untimed',
      timeLimit: 120,
      question:
        'A bar graph shows that Class A has 30 students and Class B has 25 students. How many more students are in Class A?',
      options: ['5', '10', '15', '20', 'Cannot be determined'],
      correct: 0,
      explanation:
        'The difference is found by simple subtraction: §30 - 25 = 5§ students.',
    },
    {
      type: 'untimed',
      timeLimit: 120,
      question: 'In a histogram, what does the height of each bar represent?',
      options: ['Frequency', 'Mean', 'Median', 'Range', 'Standard deviation'],
      correct: 0,
      explanation:
        'In a histogram, the height of each bar represents the frequency (count) of values in that interval.',
    },
    {
      type: 'untimed',
      timeLimit: 120,
      question: 'What is the main advantage of using a scatter plot?',
      options: [
        'Shows correlation between two variables',
        'Shows parts of a whole',
        'Shows trends over time',
        'Shows frequency distribution',
        'Shows categorical data',
      ],
      correct: 0,
      explanation:
        'Scatter plots are primarily used to visualize the relationship or correlation between two continuous variables.',
    },
    {
      type: 'untimed',
      timeLimit: 120,
      question:
        'In a double bar graph, what allows you to compare two sets of data?',
      options: [
        'Side-by-side bars',
        'Different colors only',
        'Different heights only',
        'Legend only',
        'Axis labels only',
      ],
      correct: 0,
      explanation:
        'Double bar graphs use side-by-side bars for each category, allowing direct visual comparison of two data sets.',
    },
    {
      type: 'untimed',
      timeLimit: 120,
      question: 'On a coordinate plane, what does the x-intercept represent?',
      options: [
        'Where y = 0',
        'Where x = 0',
        'The slope',
        'The origin',
        'The midpoint',
      ],
      correct: 0,
      explanation:
        'The x-intercept is where the line crosses the x-axis, meaning §y = 0§.',
    },
    {
      type: 'untimed',
      timeLimit: 120,
      question: 'A histogram is most similar to which type of graph?',
      options: [
        'Bar graph',
        'Pie chart',
        'Line graph',
        'Scatter plot',
        'Circle graph',
      ],
      correct: 0,
      explanation:
        'A histogram uses bars to display frequency distribution, similar to a bar graph but for continuous data ranges.',
    },
    {
      type: 'untimed',
      questionType: 'quantitative_comparison',
      question:
        'A table shows values 12, 15, and 18. Compare the two quantities.',
      quantityA: 'Range of the values',
      quantityB: '§6§',
      correct: 2,
      explanation:
        'Compare the two quantities directly. The two quantities are equal.',
    },
    {
      type: 'untimed',
      questionType: 'multiple_choice_multi',
      question:
        'Which graph types are best for showing change over time? Select all that apply.',
      options: [
        'Line graph',
        'Scatter plot with time on x-axis',
        'Pie chart',
        'Bar chart by year',
        'Box plot only',
      ],
      correct: [0, 1, 3],
      explanation:
        'Line graphs, time-based scatter plots, and year-by-year bar charts can show change over time.',
    },
    {
      type: 'timed',
      timeLimit: 105,
      questionType: 'numeric_entry',
      question:
        'A bar chart has category values 8, 11, and 17. Enter the total.',
      correctAnswer: '36',
      acceptedAnswers: ['36'],
      explanation: 'Add the values: §8 + 11 + 17 = 36§.',
    },
  ];

  const quickReference = [
    'Bar graphs: Compare categories',
    'Line graphs: Show trends over time',
    'Pie charts: Parts of a whole (100%)',
    'Two-way tables: choose denominator from wording',
    'Box plots: box from Q1 to Q3 contains middle 50%',
    'Always check axis labels and scales',
    'Identify trends, outliers, and patterns',
  ];

  const getStartedWithProps = {
    title: 'Need help with Graphs and Data Interpretation?',
    spaceName: 'Quant hacks',
    humanCoachTitle: 'Talk to a GRE coach',
    liwazaAITitle: 'Ask AI',
    communityTitle: 'Ask other students',
    showHumanCoach: true,
    showLiwazaAI: true,
    showCommunity: true,
    aifirstmessage:
      'I need help understanding Graphs and Data Interpretation for the GRE Quantitative section',
    aicontext:
      'Master bar graphs, line graphs, pie charts, and data interpretation strategies',
  };

  return (
    <LessonTemplate
      lessonId="graphs"
      lessonTitle="Graphs & Data Interpretation"
      lessonIcon={<FiTrendingUp />}
      lessonDescription="Master reading and interpreting bar graphs, line graphs, pie charts, and data tables with interactive visualizations"
      sections={sections}
      practiceQuestions={practiceQuestions}
      quickReference={quickReference}
      getStartedWithProps={getStartedWithProps}
      supplementalPracticeSlot={<QuantLessonPracticeSlot lessonId="graphs" />}
    />
  );
};

export default GraphsLesson;
